import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { APP_NAME } from "../config/branding";
import Seo from "../components/Seo";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-background">
      <Seo
        title="Privacy Policy | CBAIQ"
        description="How CBAIQ handles your data: uploaded agreements are never used for model training, are not externally indexed, and remain under organizational control."
        path="/privacy"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="prose prose-slate dark:prose-invert max-w-none">
          <h1 className="text-3xl font-bold mb-2">{APP_NAME} – Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Effective Date: January 2026</p>

          <div className="space-y-6">
            <section>
              <h2 className="text-xl font-semibold mb-3">Our Commitment</h2>
              <p>
                {APP_NAME} is built as a privacy-first platform. This policy explains what data we collect, 
                how we use it, and what we do not do.
              </p>
              <ul className="list-disc list-inside space-y-2 mt-3">
                <li>Your contracts remain private</li>
                <li>Your case documents remain private</li>
                <li>Your questions remain private</li>
                <li>No cross-user sharing</li>
                <li>We do not sell user data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">What We Collect</h2>
              <p className="font-medium mb-2">Account Data:</p>
              <ul className="list-disc list-inside space-y-1 mb-3">
                <li>Email address</li>
                <li>Password hash (we do not store raw passwords)</li>
                <li>Authentication tokens/sessions</li>
              </ul>
              
              <p className="font-medium mb-2">User Content:</p>
              <ul className="list-disc list-inside space-y-1 mb-3">
                <li>Uploaded CBAs</li>
                <li>Uploaded case documents (if feature enabled)</li>
                <li>Questions you ask</li>
                <li>Answers generated by the system</li>
              </ul>
              
              <p className="font-medium mb-2">Technical Data:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Minimal logs for security, error diagnosis, and performance</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">How We Use Data</h2>
              <ul className="list-disc list-inside space-y-2">
                <li>Provide the service</li>
                <li>Process documents and generate responses</li>
                <li>Maintain account security</li>
                <li>Prevent abuse</li>
                <li>Improve reliability (not for advertising)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">What We Do NOT Do</h2>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>We do not sell your data</strong></li>
                <li><strong>We do not train AI models on your content</strong></li>
                <li><strong>We do not share documents with other users</strong></li>
                <li><strong>We do not use your content for advertising</strong></li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">Security</h2>
              <ul className="list-disc list-inside space-y-2">
                <li>HTTPS transport</li>
                <li>Password hashing</li>
                <li>Token-based authentication</li>
                <li>Per-user access isolation</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">Retention</h2>
              <p>
                Data is retained while your account exists. You can request deletion at any time.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">Cookies & Sessions</h2>
              <p>
                We use minimal cookies/session storage for login state and security. No ad tracking.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">Third-Party Services</h2>
              <p>
                We may use third-party providers strictly to operate the service (AI providers, OCR tooling, 
                email delivery such as Resend, hosting). We do not allow third parties to use your content for advertising.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">Changes</h2>
              <p>We may update this policy periodically.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">Contact</h2>
              <p>
                <a href="mailto:support@cbaiq.com" className="text-primary hover:underline">
                  support@cbaiq.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
